import { createContext, useContext, ReactNode, useState, useCallback } from 'react';
import { useOnboarding } from '@/hooks/useOnboarding';
import { OnboardingCoachMarks, hasCompletedCoachMarks } from './OnboardingCoachMarks';

interface OnboardingContextType {
  startOnboarding: () => void;
  resetOnboarding: () => void;
  isFirstTime: boolean | null;
  isRunning: boolean;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboardingContext must be used within OnboardingProvider');
  }
  return context;
};

interface OnboardingProviderProps {
  children: ReactNode;
}

export const OnboardingProvider = ({ children }: OnboardingProviderProps) => {
  const { isFirstTime, resetOnboarding: resetHook } = useOnboarding();
  const [isRunning, setIsRunning] = useState(false);

  const startOnboarding = useCallback(() => {
    setIsRunning(true);
  }, []);

  const resetOnboarding = useCallback(() => {
    resetHook();
    setIsRunning(true);
  }, [resetHook]);

  return (
    <OnboardingContext.Provider value={{ 
      startOnboarding, 
      resetOnboarding, 
      isFirstTime,
      isRunning 
    }}>
      {children}
      {/* Supabase is authoritative: only show coach-marks when isFirstTime === true
          AND the local "done" flag is missing. If Supabase says not first-time,
          we never show — even if localStorage flag was cleared. */}
      {isFirstTime === true && !hasCompletedCoachMarks() && <OnboardingCoachMarks />}
    </OnboardingContext.Provider>
  );
};
